import { PushDataLayerFn } from '../../types/pushDataLayer';

/**
 * Tracking for the AIO module (CMS-1964).
 *
 * Seven items, all authored by A&A in the ticket's Airtable. Three are product
 * events and go through the shared `sendProductAnalytics` at their call sites,
 * like every other product block. The four `navigate` items share one envelope,
 * so it lives here rather than being retyped in three files.
 *
 * `component_title` is always `AIO Module/<slot>_<dynamic>`, the shape the
 * ticket spells out per item.
 */
export const GA_TITLE_PREFIX = 'AIO Module';

/**
 * Supplied by the host app, not by Strapi: `BlockMapper` passes the page's
 * `pushDataLayer` and the block's visible row (`gaIndex`). Optional throughout —
 * a consumer that renders the module without tracking (Storybook, tests) simply
 * sends nothing.
 */
export type AioModuleTracking = {
  pushDataLayer?: PushDataLayerFn;
  /** The block's visible row on the page — GA `component_index` (CMS-1693). */
  componentIndex?: string;
  /** Page slug, sent as `item_list_name` on the product events (CMS-1976). */
  slug?: string;
};

type NavigateEvent = {
  /** `faq` | `text-link` | `button`, per the item. */
  componentType: 'faq' | 'text-link' | 'button';
  /** The part after `AIO Module/` — e.g. `FAQ Accordion_<question>`. */
  title: string;
  action: 'click' | 'expand' | 'collapse';
  /** Position of the thing interacted with inside its section. */
  index: number;
};

/**
 * One `navigate` push. Returns without touching the data layer when the host
 * app supplied no `pushDataLayer`, so every caller can stay a plain handler.
 *
 * `component_index` falls back to `''` rather than being omitted: A&A read a
 * missing key as "not implemented", an empty one as "no position" — the same
 * choice ImageCarousel makes.
 */
export const sendAioNavigate = (
  { pushDataLayer, componentIndex }: AioModuleTracking,
  { componentType, title, action, index }: NavigateEvent,
): void => {
  if (!pushDataLayer) return;

  pushDataLayer({
    event: 'navigate',
    component_type: componentType,
    component_title: `${GA_TITLE_PREFIX}/${title}`,
    component_index: componentIndex ?? '',
    action,
    index: String(index),
  });
};
